import { BACKGROUND_COLOR, BRUSH_SIZE, STROKE_COLOR, WEDGE_STROKE } from './constants';
import { getLocalAngle, TAU, transformPointForSector } from './geometry';

const drawSmoothPath = (ctx, points) => {
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);

  for (let i = 1; i < points.length; i += 1) {
    const current = points[i];
    const previous = points[i - 1];
    const midX = (current.x + previous.x) / 2;
    const midY = (current.y + previous.y) / 2;

    ctx.quadraticCurveTo(previous.x, previous.y, midX, midY);
  }

  const lastPoint = points[points.length - 1];
  ctx.lineTo(lastPoint.x, lastPoint.y);
  ctx.stroke();
};

export const applyWedgeClip = (ctx, geometry) => {
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(geometry.centerX, geometry.centerY);
  ctx.lineTo(
    geometry.centerX + Math.cos(geometry.startAngle) * geometry.radius,
    geometry.centerY + Math.sin(geometry.startAngle) * geometry.radius,
  );
  ctx.arc(
    geometry.centerX,
    geometry.centerY,
    geometry.radius,
    geometry.startAngle,
    geometry.startAngle + geometry.wedgeAngle,
    false,
  );
  ctx.closePath();
  ctx.clip();
};

export const applyCircleClip = (ctx, width, height) => {
  ctx.beginPath();
  ctx.arc(width / 2, height / 2, Math.min(width, height) / 2, 0, TAU);
  ctx.closePath();
  ctx.clip();
};

const drawStrokePoints = (ctx, points, stroke) => {
  if (points.length === 0) return;

  const width = stroke.width ?? BRUSH_SIZE.default;
  const color = stroke.color ?? STROKE_COLOR;
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = width;
  if (points.length === 1) {
    ctx.beginPath();
    ctx.arc(points[0].x, points[0].y, width / 2, 0, TAU);
    ctx.fill();
    return;
  }

  drawSmoothPath(ctx, points);
};

export const renderWedgeCanvas = ({
  ctx,
  canvasSize,
  geometry,
  strokes,
  currentStroke,
}) => {
  ctx.save();
  ctx.clearRect(0, 0, canvasSize, canvasSize);
  ctx.fillStyle = BACKGROUND_COLOR;
  ctx.fillRect(0, 0, canvasSize, canvasSize);

  ctx.strokeStyle = 'rgba(12, 40, 58, 0.14)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(geometry.centerX, geometry.centerY, geometry.radius, 0, TAU);
  ctx.stroke();

  ctx.save();
  applyWedgeClip(ctx, geometry);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  strokes.forEach((stroke) => {
    if (!stroke || stroke.points.length < 1) return;
    drawStrokePoints(ctx, stroke.points, stroke);
  });

  if (currentStroke?.points?.length) {
    drawStrokePoints(ctx, currentStroke.points, currentStroke);
  }

  ctx.restore();

  ctx.strokeStyle = WEDGE_STROKE;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(geometry.centerX, geometry.centerY);
  ctx.lineTo(
    geometry.centerX + Math.cos(geometry.startAngle) * geometry.radius,
    geometry.centerY + Math.sin(geometry.startAngle) * geometry.radius,
  );
  ctx.arc(
    geometry.centerX,
    geometry.centerY,
    geometry.radius,
    geometry.startAngle,
    geometry.startAngle + geometry.wedgeAngle,
    false,
  );
  ctx.closePath();
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(geometry.centerX, geometry.centerY, 4, 0, TAU);
  ctx.fillStyle = WEDGE_STROKE;
  ctx.fill();

  ctx.restore();
};

export const renderBloomCanvas = ({
  ctx,
  canvasSize,
  geometry,
  folds,
  strokes,
  revealStartAngle,
}) => {
  ctx.save();
  ctx.clearRect(0, 0, canvasSize, canvasSize);
  applyCircleClip(ctx, canvasSize, canvasSize);
  ctx.fillStyle = BACKGROUND_COLOR;
  ctx.fillRect(0, 0, canvasSize, canvasSize);

  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  const revealGeometry =
    typeof revealStartAngle === 'number'
      ? { ...geometry, startAngle: revealStartAngle }
      : geometry;

  const renderStroke = (stroke, sectorIndex, mirror) => {
    const transformedPoints = stroke.points.map((point) => {
      const dx = point.x - geometry.centerX;
      const dy = point.y - geometry.centerY;
      const radius = Math.sqrt(dx * dx + dy * dy);
      const absoluteAngle = Math.atan2(dy, dx);
      const localAngle = getLocalAngle(absoluteAngle, geometry.startAngle);
      return transformPointForSector(
        revealGeometry,
        { radius },
        localAngle,
        mirror,
        sectorIndex,
      );
    });

    drawStrokePoints(ctx, transformedPoints, stroke);
  };

  for (let sectorIndex = 0; sectorIndex < folds; sectorIndex += 1) {
    const mirror = sectorIndex % 2 === 1;
    strokes.forEach((stroke) => {
      if (!stroke || stroke.points.length === 0) return;
      renderStroke(stroke, sectorIndex, mirror);
    });
  }

  ctx.restore();
};
